import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

export function clientCoverageCaption(coverage, clients) {
  const withOpen = Number(coverage.with_open_positions || 0);
  const inactive = Number(coverage.inactive || 0);
  const inactiveNote = inactive ? ` · ${inactive} inactive this year` : "";
  if (!clients) return inactive ? `${inactive} inactive this year` : "No active clients this year";
  if (withOpen === 0) return `All clients staffed${inactiveNote}`;
  return `${withOpen} of ${clients} with open positions${inactiveNote}`;
}

const points = (value) => `${Math.abs(value)} percentage ${Math.abs(value) === 1 ? "point" : "points"}`;

export function buildMetricCards(summary) {
  const fill = summary.fill_rate || {};
  const attention = summary.needs_attention || {};
  const coverage = summary.client_coverage || {};
  const metric = (label) => Number(summary.metrics.find((item) => item.label === label)?.value || 0);
  const hasRate = fill.rate !== null && fill.rate !== undefined;
  const gap = hasRate ? fill.target - fill.rate : null;
  const stale = (attention.open_over_10_days || []).length;
  const open = metric("Open Positions");
  const assignments = metric("Active Assignments");
  const clients = metric("Active Clients");
  const monthly = (summary.monthly_fill_rate || []).filter((item) => item.rate !== null && item.rate !== undefined);
  const previous = monthly.length > 1 ? monthly[monthly.length - 2] : null;
  const delta = previous && hasRate ? fill.rate - previous.rate : null;
  const withOpen = Number(coverage.with_open_positions || 0);

  return [
    {
      id: "fill-rate",
      label: "Fill rate",
      primary: true,
      value: hasRate ? `${fill.rate}%` : "—",
      status:
        gap === null
          ? { label: "No data", tone: "gray" }
          : gap > 0
            ? { label: "Below target", tone: "red" }
            : gap === 0
              ? { label: "On target", tone: "green" }
              : { label: "Above target", tone: "green" },
      caption:
        gap === null
          ? "No positions this school year"
          : gap > 0
            ? `${points(gap)} below the ${fill.target}% target`
            : gap === 0
              ? `Meeting the ${fill.target}% target`
              : `${points(gap)} above the ${fill.target}% target`,
      detail: hasRate ? `${fill.filled} of ${fill.total} positions filled` : null,
      trend:
        delta === null
          ? null
          : {
              direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
              label: delta === 0 ? `Steady since ${previous.month}` : `${Math.abs(delta)} pts since ${previous.month}`,
              title: delta === 0 ? `Unchanged from ${previous.month} (${previous.rate}%)` : `${points(delta)} ${delta > 0 ? "higher" : "lower"} than ${previous.month} (${previous.rate}%)`,
            },
      progress: hasRate ? { value: fill.rate, target: fill.target } : null,
    },
    {
      id: "open-positions",
      label: "Open positions",
      value: String(open),
      status: open === 0 ? { label: "Covered", tone: "green" } : stale ? { label: `${stale} aging`, tone: "amber" } : { label: "Recent", tone: "gray" },
      caption: open === 0 ? "Every position is covered" : stale ? `${stale} open more than 10 days` : "All opened within the last 10 days",
    },
    {
      id: "active-assignments",
      label: "Active assignments",
      value: String(assignments),
      status: assignments ? { label: "Live", tone: "teal" } : { label: "None", tone: "gray" },
      caption: "Confirmed or in progress",
      detail: `${fill.filled || 0} positions covered this school year`,
    },
    {
      id: "active-clients",
      label: "Active clients",
      value: String(clients),
      status: !clients ? { label: "None", tone: "gray" } : withOpen === 0 ? { label: "All staffed", tone: "green" } : { label: `${withOpen} need staff`, tone: "amber" },
      caption: clientCoverageCaption(coverage, clients),
    },
  ];
}

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

const TrendChip = ({ trend, id }) => {
  const Icon = TREND_ICON[trend.direction];
  return (
    <span className={`ops-trend is-${trend.direction}`} title={trend.title} data-testid={`kpi-${id}-trend`}>
      <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
      {trend.label}
    </span>
  );
};

const TargetTrack = ({ value, target }) => (
  <div className="ops-target-track" role="img" aria-label={`${value}% filled against a ${target}% target`} data-testid="kpi-fill-rate-track">
    <span className="ops-target-fill" style={{ width: `${Math.min(value, 100)}%` }} />
    <span className="ops-target-tick" style={{ left: `${Math.min(target, 100)}%` }} />
    <span className="ops-target-label" style={{ left: `${Math.min(target, 100)}%` }}>
      Target {target}%
    </span>
  </div>
);

export const MetricCards = ({ cards }) => (
  <section className="ops-metric-grid" aria-label="Key operational metrics" data-testid="dashboard-metrics">
    {cards.map((card) => (
      <article className={`ops-metric ${card.primary ? "is-primary" : ""}`} data-testid={`kpi-${card.id}`} key={card.id}>
        <div className="ops-metric-head">
          <p className="ops-metric-label">{card.label}</p>
          {card.status && (
            <span className={`ops-pill is-${card.status.tone}`} data-testid={`kpi-${card.id}-status`}>
              {card.status.label}
            </span>
          )}
        </div>
        <div className="ops-metric-figure">
          <strong className="ops-metric-value" data-testid={`kpi-${card.id}-value`}>
            {card.value}
          </strong>
          {card.trend && <TrendChip trend={card.trend} id={card.id} />}
        </div>
        <p className={`ops-metric-caption is-${card.status?.tone || "gray"}`} data-testid={`kpi-${card.id}-caption`}>
          {card.caption}
        </p>
        {card.detail && (
          <p className="ops-metric-detail" data-testid={`kpi-${card.id}-detail`}>
            {card.detail}
          </p>
        )}
        {card.progress && <TargetTrack {...card.progress} />}
      </article>
    ))}
  </section>
);
